import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Alert,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import Layout from '../components/Layout';

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'fixed', label: 'Fixed (scheduled)' },
  { value: 'fixedCourse', label: 'Fixed course' },
  { value: 'flexibleTime', label: 'Flexible (appointments)' },
];

function formatDuration(d) {
  if (d == null || typeof d !== 'object') return '—';
  if (d.minutes != null) return `${d.minutes} min`;
  if (d.hours != null) return `${d.hours} h`;
  return '—';
}

const OperationsTours = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [adminBase, setAdminBase] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (typeFilter) params.type = typeFilter;
        const { data } = await axios.get('/operations/bookeo-products', { params });
        const list = data.data || [];
        setRows(
          list.map((p) => ({
            id: p.productId || p.id,
            ...p,
          })),
        );
        setAdminBase(data.bookeo_admin_products_base_url || '');
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load Bookeo products');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [typeFilter]);

  const columns = useMemo(
    () => [
      { field: 'productId', headerName: 'Product ID', width: 140, minWidth: 120 },
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 220 },
      { field: 'productCode', headerName: 'Code', width: 120 },
      { field: 'type', headerName: 'Type', width: 130 },
      {
        field: 'duration',
        headerName: 'Duration',
        width: 120,
        renderCell: (params) => formatDuration(params.row.duration),
      },
      {
        field: 'shownToCustomers',
        headerName: 'Customer visible',
        width: 130,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.shownToCustomers ? 'Yes' : 'No'}
            color={params.row.shownToCustomers ? 'success' : 'default'}
          />
        ),
      },
      {
        field: 'apiBookingsAllowed',
        headerName: 'API bookings',
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.apiBookingsAllowed ? 'Yes' : 'No'}
            color={params.row.apiBookingsAllowed ? 'success' : 'default'}
          />
        ),
      },
      {
        field: 'detail',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button size="small" onClick={() => { setDetailRow(params.row); setDetailOpen(true); }}>
            Details
          </Button>
        ),
      },
    ],
    [],
  );

  const manageUrl = (productId) => {
    if (adminBase && productId) {
      return adminBase.split('{productId}').join(productId);
    }
    return 'https://www.bookeo.com/';
  };

  return (
    <Layout>
      <Container maxWidth={false} sx={{ width: '100%' }}>
        <Typography variant="h4" gutterBottom fontWeight={700}>
          Tours (Bookeo products)
        </Typography>
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          <strong>Read-only.</strong> Bookeo&apos;s public API only allows listing products (
          <code>GET /v2/settings/products</code>); there is no documented create/update endpoint. To
          add or edit products in Bookeo, use the button below or open Bookeo directly. Optionally set{' '}
          <code>BOOKEO_ADMIN_PRODUCTS_BASE_URL</code> on the server (use <code>{'{productId}'}</code>{' '}
          in the URL for per-product links).
        </Alert>
        {!adminBase && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Set <code>BOOKEO_ADMIN_PRODUCTS_BASE_URL</code> on the server so &quot;Manage in
            Bookeo&quot; opens your product settings. Until then, links use the Bookeo home page.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            select
            size="small"
            label="Product type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Paper>
        <Paper sx={{ height: 640, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pageSize={25}
            rowsPerPageOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
          />
        </Paper>
        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Product details</DialogTitle>
          <DialogContent dividers>
            {detailRow && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Button
                    component="a"
                    href={manageUrl(detailRow.productId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    endIcon={<OpenInNewIcon />}
                  >
                    Manage in Bookeo
                  </Button>
                </Box>
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{ overflow: 'auto', maxHeight: 480, fontSize: '0.75rem' }}
                >
                  {JSON.stringify(detailRow, null, 2)}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default OperationsTours;
